import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { message } from "antd";
import i18n from "~/i18n";
import * as APITypes from "~/API";
import { INTENTS, ROUTES, loadCookiesData } from "~/utils";
import { apiClient, AddTenantPageLoaderResult } from "~/services/apiClient";

export const AddTenantPageLoader = async ({ request }: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  const result = await apiClient.loader<AddTenantPageLoaderResult>(
    "add-tenant-page",
    {
      userID: user.uid,
      ownerID,
      ownerType,
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load add tenant page:", result.error);
    throw new Error(result.error ?? "Failed to load add tenant page");
  }

  return {
    canCreateTenant: result.data.canCreateTenant,
  };
};

export type AddTenantPageLoaderType = {
  canCreateTenant: boolean;
};

export const AddTenantPageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");

  const formData = await request.formData();
  const intent = formData.get("intent");
  switch (intent) {
    case INTENTS._add_tenant: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.Tenant>(
        INTENTS._add_tenant,
        { type: APITypes.TenantType.ShadowTenant, ...payload }
      );
      if (!result.success || !result.data) {
        const errorMessage =
          typeof result.error === "string"
            ? result.error
            : i18n.t("Error_creating_tenant");
        message.error(errorMessage);
        console.error("Add tenant failed:", result.error);
        return { success: false, error: result.error ?? errorMessage };
      }

      message.success(i18n.t("Tenant_created_successfully"));

      const { searchParams } = new URL(request.url);
      const entityType = (searchParams.get("entityType") ||
        undefined) as APITypes.EntityType;
      const entityID = searchParams.get("entityID") || undefined;

      if (entityID) {
        return redirect(`/${entityType}/${entityID}`);
      }
      return redirect(ROUTES.tenantTable);
    }
  }
  return {};
};
